// Dev-only end-to-end test bridge. Exposes `window.__test` so a
// Playwright (or any in-page) harness can drive a solo sandbox match
// through the SAME host methods the UI calls, then read game state to
// assert card behaviour. Installed only in dev builds (isDevBuild gate
// in boardLifecycle), so it never ships to players.
//
// Design: every action funnels through an existing host primitive. No
// bespoke game logic lives here. `playFromHand` mirrors the real drop
// commit (position a mesh at the target cell, then _finalizeFreshPlayCommit),
// so on_play automation fires exactly as a human drop would. Setup
// helpers (`give`, `place`) seed state without firing on_play.

import * as THREE from 'three';
import { sendDevLog } from '../../../utils/game/socketClient';
import { createCardInstance, createPile } from '../../../utils/game/gameState';
import { getGameGrid, getSpawnPoint } from '../../../utils/game/spawnConfig';
import { classifyCell } from '../../../utils/game/engine/core/positional';
import { sanitizeUnitHandsForSeat, effectiveSiteThresholds, aura4AnchorWorldPoint, anchorCoveredCells } from '../../../utils/game/engine/core';
import { getEffectiveStatuses } from '../../../utils/game/engine/automation';
import { checkPresentation } from './presentationInvariants';
import { k } from '../../../localization/t';
import { logEntryText } from './logbookChat';
import { overrideLanguage } from '../../../localization/preferences';

function catalogRow(host, name) {
  const cards = host.props?.sorceryCards || [];
  return cards.find((c) => c.name === name) || null;
}

// Real per-seat spawn anchor for a pile, so seeded piles sit where the
// live deck-spawn path (gameLifecycle.spawnSelectedDeck) would put them
// instead of stacked at the origin. P2's piles use the mirrored `*2` keys.
const PILE_SPAWN_KEY = { Spellbook: 'spellbook', Atlas: 'atlas', Collection: 'collection', Cemetery: 'cemetery' };
function pileAnchor(host, name, owner) {
  const key = PILE_SPAWN_KEY[name] || name.toLowerCase();
  const suffix = owner === 'p2' ? '2' : '';
  return getSpawnPoint(host.state.spawnConfig, `${key}${suffix}`);
}

// Mint a card instance from the RAW catalog. createCardInstance expects a
// normalized catalog (subTypes/elements as arrays under `traits`/`elements`),
// but the raw sorcery-cards.json stores them as strings. So a naive mint
// loses subtypes/elements and breaks subtype/element filters. Backfill them.
function mint(host, cat, rotated) {
  const card = createCardInstance(cat, null, rotated);
  const toArr = (v) => (Array.isArray(v) ? v : v ? [v] : []);
  if (!card.subTypes?.length) {
    card.subTypes = toArr(cat.subTypes).map((s) => String(s));
  }
  if (!card.elements?.length) {
    card.elements = toArr(cat.elements).map((e) => String(e?.name || e).toLowerCase());
  }
  return card;
}

// Resolve an id-or-name to a live card instance, searching the board
// meshes first, then both hands. Lets scenarios refer to cards by name.
function findCard(host, idOrName) {
  const match = (c) => c && (c.id === idOrName || c.name === idOrName);
  for (const [, mesh] of host.meshes) {
    const card = mesh?.userData?.cardInstance;
    if (match(card)) return card;
    // Equipped artifacts (Wiccan Tools, Lance) live on the bearer, not
    // in the meshes map.
    const eq = (card?.equippedCards || []).find(match);
    if (eq) return eq;
  }
  for (const arr of [host.state.handCards, host.state.handCards2]) {
    const hit = (arr || []).find(match);
    if (hit) return hit;
  }
  for (const entry of Object.values(host.state.gameState?.unitHands || {})) {
    const hit = (entry.spells || []).find(match);
    if (hit) return hit;
  }
  return null;
}

function cardSummary(card) {
  const auraDelta = card._auraStatDelta || 0;
  return {
    id: card.id,
    name: card.name,
    cardId: card.cardId,
    type: card.type,
    faceDown: !!card.faceDown,
    rotated: !!card.rotated,
    tapped: !!card.tapped,
    subsurface: card.subsurface || null,
    statuses: Array.isArray(card.statuses) ? [...card.statuses] : [],
    equipment: (card.equippedCards || []).map((c) => c.name),
    isToken: !!card.isToken,
    subTypes: Array.isArray(card.subTypes) ? [...card.subTypes] : [],
    power: (card.currentAttack ?? card.baseAttack ?? 0) + auraDelta,
    life: (card.currentLife ?? card.baseLife ?? 0) + auraDelta,
    movement: card._movementBonus || 0,
    counters: card._counters || 0,
  };
}

export function buildTestBridge(host) {
  const ownerRotated = (owner) => owner === 'p2';

  const api = {
    host,

    // ── Scene digest (agent vision) ──────────────────────────────
    // A machine-readable description of what the VIEWER currently sees,
    // read from ACTUAL render output. Mesh geometry, the texture really
    // applied, live DOM. Never re-derived from game state. A renderer
    // bug therefore shows up as a wrong digest instead of being laundered
    // through the same code that caused it (`stateFlags` rides along per
    // card precisely so a digest reader can spot render-vs-state drift).
    // Agents and GUI specs read this instead of screenshots.
    describeScene() {
      const norm = (rad) => {
        const deg = Math.round((rad * 180) / Math.PI);
        return ((deg % 360) + 360) % 360;
      };
      const yawBucket = (deg) => [0, 90, 180, 270].reduce((best, a) => {
        const d = (x) => Math.min(Math.abs(deg - x), 360 - Math.abs(deg - x));
        return d(a) < d(best) ? a : best;
      }, 0);
      // The texture really bound to a material. A FOIL card front is a
      // ShaderMaterial. Its art lives in the uTex uniform, not `.map`. So a
      // `.map`-only read falls through to the back material and reports every
      // foil card on the board as showing a card back.
      const texSrc = (mat) => {
        const tex = mat?.map || mat?.uniforms?.uTex?.value;
        const src = tex?.image?.src || tex?.image?.dataset?.sourceUrl || tex?.source?.data?.src;
        return src ? String(src).split('/').pop().split('?')[0] : null;
      };
      const artOf = (mesh) => {
        const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        for (const mat of mats) {
          const src = texSrc(mat);
          if (src) return src;
        }
        return null;
      };

      const describeCard = (id, mesh, cosmetic) => {
        const card = mesh.userData.cardInstance || {};
        const face = mesh.rotation.x > 0 ? 'back' : 'front';
        const yawDeg = norm(mesh.rotation.z);
        const at = host._cellAtWorld?.(mesh.position.x, mesh.position.z) || null;
        return {
          id,
          cell: at ? { col: at.col, row: at.row } : null,
          pos: { x: +mesh.position.x.toFixed(2), y: +mesh.position.y.toFixed(2), z: +mesh.position.z.toFixed(2) },
          face,
          shownName: face === 'front' ? (card.name ?? null) : null,
          shownArt: artOf(mesh),
          yawDeg,
          // 0 = upright for P1's camera, 180 = rendered as the opponent's,
          // 90/270 = the tapped quarter-turn (a face-down card adds 180).
          yawBucket: yawBucket(yawDeg),
          cosmetic: !!cosmetic,
          stateFlags: {
            tapped: !!card.tapped, rotated: !!card.rotated,
            faceDown: !!card.faceDown, subsurface: card.subsurface || null,
            statuses: card.statuses || [],
          },
        };
      };

      const board = [];
      for (const [id, mesh] of host.meshes) {
        if (mesh?.userData?.type !== 'card') continue;
        board.push(describeCard(id, mesh, false));
      }
      // Cosmetic overlay cards (the Howl from Beyond reveal procession) are
      // added straight to the scene root, never to host.meshes. They are not
      // board STATE, but the player is looking right at them, so a digest that
      // skipped them would be blind to exactly the reveal it needs to describe.
      for (const obj of host.scene?.scene?.children || []) {
        if (obj?.userData?.type !== 'card' || !obj.userData.cosmetic) continue;
        board.push(describeCard(obj.userData.cardInstance?.id ?? null, obj, true));
      }

      // Piles (Spellbook / Atlas / Cemetery / Banished / Collection): the
      // RENDERED depth is read directly from the compressed box geometry;
      // the presentation invariant compares it to the server count. topArt is the texture
      // really applied to the pile's top face (cemetery = top card face-up,
      // everything else = card back). stateCount rides along so a digest
      // reader can spot a stack rendering taller/shorter than the state says.
      const piles = [];
      for (const mesh of host.pileMeshes?.values?.() || []) {
        if (mesh?.userData?.type !== 'pile') continue;
        const pile = mesh.userData.pile || {};
        const depth = mesh.geometry?.parameters?.depth;
        const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        piles.push({
          id: pile.id ?? null,
          name: pile.name ?? null,
          renderedDepth: typeof depth === 'number' ? depth : null,
          stateCount: pile.cards?.length ?? null,
          topArt: texSrc(mats[4]),
          yawDeg: norm(mesh.rotation.z),
          pos: { x: +mesh.position.x.toFixed(2), z: +mesh.position.z.toFixed(2) },
        });
      }

      const domHands = [...document.querySelectorAll('[data-tutorial="board-hand"], [data-tutorial="board-atlas-hand"]')]
        .map((el) => ({
          kind: el.getAttribute('data-tutorial'),
          cards: [...el.querySelectorAll('img')].map((img) => ({
            shownArt: String(img.src).split('/').pop().split('?')[0],
            alt: img.alt || null,
          })),
        }));

      // Unit-hand badges: the fanned SPELLS (id-tagged, so a digest reader can
      // pair each rendered face with the projection's spell) kept separate from
      // the badge's own owner-unit art. Both are <img>s in the same badge, and
      // "which one is showing a card back" is the whole question.
      const imgSrc = (img) => (img ? String(img.src).split('/').pop().split('?')[0] : null);
      const unitHands = [...document.querySelectorAll('[data-unit-badge]')].map((el) => ({
        bearer: el.getAttribute('data-unit-badge'),
        transform: getComputedStyle(el).transform,
        ownerArt: imgSrc(el.querySelector('[data-unit-badge-art] img')),
        cards: [...el.querySelectorAll('[data-unit-spell]')].map((slot) => {
          const img = slot.querySelector('img');
          return {
            id: slot.getAttribute('data-unit-spell'),
            shownArt: imgSrc(img),
            alt: img?.alt || null,
            rotated: /rotate/.test(img?.style?.transform || ''),
          };
        }),
      }));

      const toasts = [...document.querySelectorAll('[data-sonner-toast], [role="status"]')]
        .map((el) => el.textContent?.trim()).filter(Boolean);

      return {
        viewer: {
          pilotSeat: host.state.activePlayerNum === 2 ? 'p2' : 'p1',
          isHost: !!host.state.isHost,
          regionView: host.state.regionView || 'surface',
        },
        turn: {
          currentTurn: host.state.currentTurn,
          phase: host.state.currentPhase,
          matchPhase: host.state.matchPhase,
        },
        board,
        piles,
        domHands,
        unitHands,
        prompts: {
          pendingServerChoice: host.state.pendingServerChoice
            ? { kind: host.state.pendingServerChoice.kind, promptId: host.state.pendingServerChoice.promptId }
            : null,
          pendingTarget: host.state.pendingTarget ? { op: host.state.pendingTarget.op } : null,
          pendingCombat: host.state.gameState?.pendingCombat
            ? { phase: host.state.gameState.pendingCombat.phase, kind: host.state.gameState.pendingCombat.prompt?.kind }
            : null,
          focusedCell: host.state.focusedCell || null,
        },
        toasts,
        logTail: (host.state.gameLog || []).slice(-8).map((e) => `${e.kind}:${logEntryText(e)}`),
      };
    },

    // Presentation invariants: the expected PICTURE derived from the server
    // projection, checked against the digest's actual render output. Returns
    // the violation strings (empty = the scene looks like the projection says
    // it must). See controllers/presentationInvariants.js.
    checkPresentation() {
      return checkPresentation(api.describeScene(), host._authMirror?.getState?.() || null, {
        isSolo: !!host.props?.isCasualSandbox,
        localSeat: host.state.isHost ? 'p1' : 'p2',
        cameraRotated: host._localCameraRotated?.() ?? false,
      });
    },

    // Dump the digest into the match's dev logs (client-<seat>.jsonl) so
    // agents can read what the player SAW at this checkpoint from the log
    // files alone. Returns the digest for in-test assertions too.
    dumpScene(label = '') {
      const scene = api.describeScene();
      const matchId = host.props?.roomCode || host._matchKey?.() || null;
      if (matchId) sendDevLog(matchId, { type: 'scene', label, scene });
      return scene;
    },

    // ── Readers ──────────────────────────────────────────────────
    cardLocationDiagnostics(id) {
      const m = host._authMirror?.getState?.();
      return { card: m?.cards?.[id], unitHands: m?.unitHands, localUnitHands: host.state.gameState.unitHands,
        mesh: host.meshes.has(id), drawing: host._drawAnimIds?.has(id),
        hand1: api.hand('p1').map((c) => c.id), hand2: api.hand('p2').map((c) => c.id) };
    },

    snapshot() {
      return {
        turn: host.state.currentTurn,
        phase: host.state.currentPhase,
        turnNumbers: { ...host.state.turnNumbers },
        activePlayerNum: host.state.activePlayerNum,
        hand: api.hand('p1'),
        hand2: api.hand('p2'),
        table: api.table(),
        piles: api.piles(),
        resources: {
          p1: host.playerResources.snapshot('p1'),
          p2: host.playerResources.snapshot('p2'),
        },
        prompts: api.prompts(),
        scry: host.state.scry ? { ...host.state.scry } : null,
        pendingPileSelect: host.state.pendingPileSelect
          ? { kind: host.state.pendingPileSelect.kind, max: host.state.pendingPileSelect.max } : null,
        pendingTarget: host.state.pendingTarget
          ? { op: host.state.pendingTarget.op } : null,
        pendingCellTarget: host.state.pendingCellTarget
          ? { op: host.state.pendingCellTarget.op } : null,
      };
    },

    hand(owner = 'p1') {
      const arr = owner === 'p2' ? host.state.handCards2 : host.state.handCards;
      return (arr || []).map(cardSummary);
    },

    table() {
      const out = [];
      for (const [, mesh] of host.meshes) {
        const card = mesh?.userData?.cardInstance;
        if (!card) continue;
        const rawCell = host._cellAtWorld(mesh.position.x, mesh.position.z);
        // Slim to { col, row }. Scenarios assert `.cell` against a plain
        // { col, row } literal; the raw _cellAtWorld carries centerX/width/
        // corners that would break a toEqual.
        const cell = rawCell ? { col: rawCell.col, row: rawCell.row } : null;
        out.push({ ...cardSummary(card), cell });
      }
      return out;
    },

    piles() {
      const out = {};
      for (const pile of host.state.gameState.piles || []) {
        out[`${pile.name}:${pile.rotated ? 'p2' : 'p1'}`] = (pile.cards || []).map((c) => c.name);
      }
      return out;
    },

    pile(name, owner = 'p1') {
      const pile = host.findPileByNameForPlayer(name, owner === 'p2' ? 2 : 1);
      return (pile?.cards || []).map(cardSummary);
    },

    // The owner's cemetery, found by its stable id (cemetery-p1/p2). The
    // real pile is named "Cemetery P1"/"P2", so a plain name lookup via
    // pile('Cemetery') misses it.
    cemetery(owner = 'p1') {
      const id = owner === 'p2' ? 'cemetery-p2' : 'cemetery-p1';
      const p = (host.state.gameState.piles || []).find((x) => x.id === id);
      return (p?.cards || []).map(cardSummary);
    },

    // The owner's Banished pile (id banished-p1/p2, name "Banished P1/P2").
    // Like cemetery(), found by id. A plain name lookup misses it.
    banished(owner = 'p1') {
      const id = owner === 'p2' ? 'banished-p2' : 'banished-p1';
      const p = (host.state.gameState.piles || []).find((x) => x.id === id);
      return (p?.cards || []).map(cardSummary);
    },

    // Owner-locked unit hands, keyed by owner instance id:
    // { [ownerId]: { controller, count } }. The public summary both seats
    // always see (no faces).
    unitHands() {
      const out = {};
      for (const [ownerId, entry] of Object.entries(host.state.gameState?.unitHands || {})) {
        const owner = host.meshes.get(ownerId)?.userData?.cardInstance;
        const catalogArt = owner?.cardId ? host._getFullCard(owner.cardId)?.printings?.[0]?.image_url : null;
        out[ownerId] = {
          controller: entry.controller,
          count: entry.spells.length,
          // Badge-art resolution mirror (UnitHandBadges.groupsFor): the
          // value the badge would render. null → it falls back to a back.
          ownerArt: entry.ownerArt || null,
          badgeArt: entry.ownerArt || owner?.imageUrl || catalogArt || null,
          ownerInMeshes: !!owner,
        };
      }
      return out;
    },

    // A single unit hand as the given seat would see it: real faces when
    // the seat controls the unit, otherwise face-down backs (count public,
    // identities hidden). Exercises the per-seat sanitization.
    unitHand(ownerIdOrName, viewer = 'p1') {
      const owner = findCard(host, ownerIdOrName);
      const ownerId = owner?.id || ownerIdOrName;
      const view = sanitizeUnitHandsForSeat(
        host.state.gameState?.unitHands || {}, viewer, (s) => ({ id: s.id, faceDown: true }),
      );
      const entry = view[ownerId];
      if (!entry) return null;
      return {
        controller: entry.controller,
        spells: entry.spells.map((s) => (s.faceDown ? { id: s.id, faceDown: true } : cardSummary(s))),
      };
    },

    // Recent action-log lines (newest last). Lets a test assert the verb the
    // engine logged. E.g. a magic logs "Cast X", never "Played X on [cell]".
    logTail(n = 8) {
      return (host.state.gameLog || []).slice(-n).map((e) => logEntryText(e));
    },
    // The logbook as the DOM shows it: what the player actually reads.
    logbookText() {
      return host.logbookScrollRef?.textContent || '';
    },
    // Switch the app language for this run (in memory, the saved choice is
    // untouched); the whole tree re-renders, the log included.
    setLanguage(code) { overrideLanguage(code); },

    // Magics currently played to the storyline (cast, resolving, not yet in
    // the cemetery). Read mid-dwell to assert a spell is on the storyline.
    castingSpells() {
      return (host.state.castingSpells || []).map((c) => ({ id: c.id, cardName: c.cardName }));
    },

    // Override the on-storyline dwell (ms) before a cast magic leaves to the
    // cemetery. Defaults to 0 under test (synchronous). Set >0 to observe the
    // storyline→cemetery sequence.
    setCastDwell(ms) { host._castDwellMs = ms; },


    prompts() {
      return (host.state.pendingPrompts || []).map((p) => ({
        id: p.id,
        promptType: p.promptType,
        controller: p.controller,
        cardName: p.cardName || p.cardInstance?.name,
        choices: (p.choices || []).map((c) => ({ value: c.value, label: c.label })),
      }));
    },

    resources(player = 'p1') {
      return {
        ...host.playerResources.snapshot(player),
        life: host.playerResources.getLife(player),
        mana: host.playerResources.getMana(player),
      };
    },

    // The Apex of Babel free-cast window: true while the player's next
    // spell this turn is free (host._freeCastGrant[player]).
    freeCastGrant(player = 'p1') {
      return !!host._freeCastGrant?.[player];
    },

    // Engine game-over verdict: { over, winner }. _gameOverWinner is set by
    // declareGameOver (death blow, Immortal Throne win, …).
    gameResult() {
      return { over: !!host._gameOverWinner, winner: host._gameOverWinner || null };
    },

    // Set a card's level/counter total directly (The Immortal Throne test
    // primes it to 7 so a single cost-7 cast reaches level 8).
    setCounters(idOrName, n) {
      const card = findCard(host, idOrName);
      if (!card) throw new Error(`setCounters: "${idOrName}" not found`);
      card._counters = n;
      host._broadcastCardCounters?.(card.id, n);
      host._refreshCounterAnchorFor?.(card.id);
      return card.id;
    },

    // Toggle a card's status keyword to `active` via the REAL menu path
    // (host.toggleCardStatus → applyStatusToggle + badge rebuild + aura
    // recompute), exactly as the player's radial menu does. Use to assign
    // 'disabled' / 'silenced' / 'airborne' etc. and verify the downstream
    // rule (a disabled card loses all keywords + automations). No-op if the
    // card is already in the requested state.
    setStatus(idOrName, key, active = true) {
      const card = findCard(host, idOrName);
      if (!card) throw new Error(`setStatus: "${idOrName}" not found`);
      const isActive = getEffectiveStatuses(card).includes(key);
      if (isActive !== active) host.toggleCardStatus(card, key);
      return card.id;
    },

    // Lower a player's life to `n` (must be below current) so a later heal /
    // damage is observable. Life is capped at the starting total, so heals
    // are invisible at full life.
    setLife(player, n) {
      const cur = host.playerResources.getLife(player);
      if (n < cur) host.playerResources.loseLife(player, cur - n, 'test');
      return host.playerResources.getLife(player);
    },

    // Per-turn site-placement grants for a player (Landmass/Overflow's
    // "you may play a land/water site"). Reads host._siteGrants.
    siteGrants(player = 'p1') {
      return host._siteGrants?.[player] || 0;
    },

    // True if the player's Avatar is flagged to skip its next turn (Vaults
    // of Zul's skip_next_turn). The flag lives on the Avatar.
    skipNextTurn(player = 'p1') {
      const avatar = host._findAvatarForPlayer(player === 'p2' ? 2 : 1);
      return !!avatar?._skipNextTurn;
    },

    // True if a card has the this-turn threshold waiver (Wiccan Tools'
    // "their spells require no threshold").
    thresholdWaiver(idOrName) {
      const card = findCard(host, idOrName);
      return !!card?._thresholdWaiverThisTurn;
    },

    // Effective per-site threshold contribution { air, earth, fire, water }
    // after auras (suppression, multiplier, flooded floor). The exact value
    // the affinity HUD reads. Use to assert Atlantean Fate / Drought / Sow.
    siteThresholds(idOrName) {
      const card = findCard(host, idOrName);
      if (!card) return null;
      return effectiveSiteThresholds(card, host._getFullCard(card.cardId));
    },

    // A card's effective status/keyword set after auras + suppression +
    // silence (the badge/rules source of truth). Use to assert aura-granted
    // statuses the manual `statuses` summary can't see (flooded, silenced).
    effStatuses(idOrName) {
      const card = findCard(host, idOrName);
      return card ? getEffectiveStatuses(card) : [];
    },

    // The cells an aura covers, as `col,row` keys (its "affected" region).
    // Reads the stamped _auraAnchor + _auraShape. Use to confirm a region
    // aura landed where the scenario intended.
    coveredCells(idOrName) {
      const card = findCard(host, idOrName);
      return card?._auraAnchor ? anchorCoveredCells(card._auraAnchor, card._auraShape?.shape) : [];
    },

    // Query a unit's current board position, classified from ITS controller's
    // perspective: { col, row, isCorner, isOuterColumn, isOuterRow, isEdge,
    // isBackRow, isFrontRow, rankFromBack }. The general positional-query
    // primitive. Reads live position any time, not just on a move event.
    // Returns null if the card has no mesh on the board.
    positionOf(idOrName) {
      const card = findCard(host, idOrName);
      const mesh = card && host.meshes.get(card.id);
      if (!mesh) return null;
      const cell = host._cellAtWorld(mesh.position.x, mesh.position.z);
      const grid = getGameGrid(host.state.spawnConfig);
      return classifyCell(cell, grid, !!card.rotated);
    },

    // ── Setup (no on_play) ───────────────────────────────────────
    give(cardName, { owner = 'p1' } = {}) {
      const row = catalogRow(host, cardName);
      if (!row) throw new Error(`give: unknown card "${cardName}"`);
      const card = mint(host, row, ownerRotated(owner));
      host.addToHand(card);
      return card.id;
    },

    place(cardName, { col, row, owner = 'p1', subsurface = null } = {}) {
      const cat = catalogRow(host, cardName);
      if (!cat) throw new Error(`place: unknown card "${cardName}"`);
      const card = mint(host, cat, ownerRotated(owner));
      const world = host._cellCenterWorld(col, row);
      card.x = world.x; card.y = 0; card.z = world.z;
      if (subsurface) card.subsurface = subsurface;
      // silent: seed the card WITHOUT firing its Genesis / site-play
      // effects (addCardToTable otherwise runs applySitePlayEffects, which
      // would open a stray draw/Genesis prompt for every placed card).
      host.addCardToTable(card, false, { silent: true });
      return card.id;
    },

    setMana(player, amount) {
      host.playerResources.setMana(player, amount);
      host.forceUpdate?.();
    },

    // Seed (or top up) a named pile for an owner with the given cards.
    // A new pile is created at its real per-seat spawn anchor (see
    // pileAnchor) so the seeded board matches the live deck layout instead
    // of stacking every pile at the origin.
    seedPile(name, cardNames, { owner = 'p1' } = {}) {
      const cards = cardNames.map((n) => {
        const cat = catalogRow(host, n);
        if (!cat) throw new Error(`seedPile: unknown card "${n}"`);
        return mint(host, cat, ownerRotated(owner));
      });
      let pile = host.findPileByNameForPlayer(name, owner === 'p2' ? 2 : 1);
      if (pile) {
        pile.cards.push(...cards);
      } else {
        const a = pileAnchor(host, name, owner);
        pile = createPile(name, cards, a.x, a.z, ownerRotated(owner));
        host.state.gameState.piles.push(pile);
      }
      host.updatePileMeshes?.();
      return pile.id;
    },

    // Establish the faithful solo board premise: BOTH seats get their two
    // deck libraries (Spellbook + Atlas) as real, correctly-positioned,
    // correctly-owned piles. The way the live deck-spawn path lays out two
    // decks, one per seat. Created empty (deterministic. Tests seed exact
    // contents via seedPile, which appends into these). Cemetery/Collection
    // stay lazily created by the engine. Idempotent. Returns the pile ids.
    setupBoard() {
      const ids = [];
      for (const owner of ['p1', 'p2']) {
        for (const name of ['Spellbook', 'Atlas']) {
          let pile = host.findPileByNameForPlayer(name, owner === 'p2' ? 2 : 1);
          if (!pile) {
            const a = pileAnchor(host, name, owner);
            pile = createPile(name, [], a.x, a.z, ownerRotated(owner));
            host.state.gameState.piles.push(pile);
          }
          ids.push(pile.id);
        }
      }
      host.updatePileMeshes?.();
      return ids;
    },

    // Read a pile's live position + ownership + card count. Lets a test
    // assert the two seats' decks sit at their real, mirrored anchors.
    pilePosition(name, owner = 'p1') {
      const p = host.findPileByNameForPlayer(name, owner === 'p2' ? 2 : 1);
      return p ? { x: p.x, z: p.z, rotated: p.rotated, count: p.cards.length } : null;
    },

    drawCards(owner = 'p1', n = 1, pileName = 'Spellbook') {
      const pile = host.findPileByNameForPlayer(pileName, owner === 'p2' ? 2 : 1);
      if (!pile) throw new Error(`drawCards: no ${pileName} for ${owner}`);
      for (let i = 0; i < n; i++) host.drawCard(pile.id, { skipLog: true });
    },

    // Pull a NAMED card out of a pile into its owner's hand through the real
    // tutor path (pile search → take card: host.takeCardFromPile, which emits
    // the authoritative MOVE_TO_ZONE). Deterministic deck setup for GUI
    // scenarios. The shuffled opening hand no longer decides what's castable.
    takeFromPile(pileName, cardName, owner = 'p1') {
      const pile = host.findPileByNameForPlayer(pileName, owner === 'p2' ? 2 : 1);
      const card = (pile?.cards || []).find((c) => c.name === cardName || c.id === cardName);
      if (!card) throw new Error(`takeFromPile: "${cardName}" not in ${pileName} (${owner})`);
      host.takeCardFromPile(pile, card);
      return card.id;
    },

    // Where a mesh (or a cell) currently projects on screen, in viewport CSS
    // pixels. So a Playwright harness can drive REAL canvas clicks (mousedown
    // → raycast → the same click path a player takes) at the right spot.
    screenPos(query) {
      const cam = host.scene?.camera;
      const canvas = host.scene?.renderer?.domElement;
      if (!cam || !canvas) return null;
      let world;
      if (query?.id || typeof query === 'string') {
        const card = findCard(host, query?.id || query);
        const mesh = card && host.meshes.get(card.id);
        if (!mesh) return null;
        world = { x: mesh.position.x, y: mesh.position.y, z: mesh.position.z };
      } else if (typeof query?.col === 'number' && typeof query?.row === 'number') {
        const w = host._cellCenterWorld(query.col, query.row);
        if (!w) return null;
        world = { x: w.x, y: 0.05, z: w.z };
      } else {
        return null;
      }
      const v = new THREE.Vector3(world.x, world.y, world.z).project(cam);
      const rect = canvas.getBoundingClientRect();
      return {
        x: rect.left + ((v.x + 1) / 2) * rect.width,
        y: rect.top + ((1 - v.y) / 2) * rect.height,
      };
    },

    // ── Actions ──────────────────────────────────────────────────
    // Play a card from hand the way the UI does. Magic spells resolve
    // and go to the cemetery (no board mesh), so they route through the
    // real on_play automation (cast pipeline / standalone resolver).
    // Everything else (Minion / Site / Artifact / Aura) takes the
    // faithful fresh-play commit: position a mesh at the cell, then run
    // _finalizeFreshPlayCommit. Firing on_play automation.
    // `cornerAnchor: { col, row }` drops an INTERSECTION_4 region aura on
    // an exact 2×2 corner so its covered cells are deterministic (a bare
    // cell-center drop is equidistant to four corners). The covered cells
    // are (col-1,row-1),(col,row-1),(col-1,row),(col,row).
    playFromHand(idOrName, { col, row, cornerAnchor } = {}) {
      const card = findCard(host, idOrName);
      if (!card) throw new Error(`playFromHand: card not in hand "${idOrName}"`);
      const world = cornerAnchor
        ? aura4AnchorWorldPoint(getGameGrid(host.state.spawnConfig), cornerAnchor.col, cornerAnchor.row)
        : host._cellCenterWorld(col, row);
      if (!world) throw new Error(`playFromHand: no drop point for "${idOrName}"`);
      card.x = world.x; card.y = 0; card.z = world.z;
      const full = host._getFullCard(card.cardId);
      const controller = card.rotated ? 'p2' : 'p1';
      // Animist armed "next magic → Spirit": fall through to the realm-entry
      // play path so addCardToTable's transform flips the magic into a Spirit
      // Minion. Unarmed magics take the storyline cast shortcut below.
      if (full?.type === 'Magic' && !host._animistSpiritArm?.[controller]) {
        host.removeFromHand(card);
        // hand → storyline → the cell it was cast to. Returns a promise the
        // harness awaits; the bridge sets _castDwellMs=0 so it resolves now.
        return host._castMagicFromHand(card);
      }
      host.removeFromHand(card);
      // Stage the play exactly like the real drag-from-hand path: a staged
      // addCardToTable spawns the mesh WITHOUT firing the "Played" log or
      // site play-effects (mana bump, affinity, Genesis), then
      // _finalizeFreshPlayCommit fires them once. A non-staged add would
      // fire them here AND again in finalize, double-granting site mana.
      host.addCardToTable(card, true, { staged: true });
      const mesh = host.meshes.get(card.id);
      if (!mesh) throw new Error(`playFromHand: mesh not spawned for "${card.name}"`);
      host._finalizeFreshPlayCommit(card, { mesh });
      return card.id;
    },

    // Cast a spell out of a unit hand (the drag-from-badge equivalent).
    // The owner is the predetermined caster, so the spellcaster
    // requirement is bypassed (routeSpellPlay's owner-locked path). The
    // cast commit removes the spell from the unit hand. Magics dwell on the
    // storyline first; every type ends up in the realm at col,row.
    castFromUnitHand(ownerIdOrName, spellName, { col, row } = {}) {
      const owner = findCard(host, ownerIdOrName);
      const ownerId = owner?.id || ownerIdOrName;
      const entry = host.state.gameState?.unitHands?.[ownerId];
      const spell = (entry?.spells || []).find((s) => s.name === spellName || s.id === spellName);
      if (!spell) throw new Error(`castFromUnitHand: "${spellName}" not in unit hand "${ownerId}"`);
      const full = host._getFullCard(spell.cardId);
      const world = host._cellCenterWorld(col, row);
      spell.x = world.x; spell.y = 0; spell.z = world.z;
      // A magic takes the storyline cast path and lands on this cell once it
      // resolves, so it needs the same drop coords as a realm-entry play.
      if (full?.type === 'Magic') {
        return host._castMagicFromHand(spell);
      }
      // Stage the minion like a drag, then run the SAME fresh-play gating the
      // drag-drop applies (owner-summon restriction "summoned here", positional
      // restrictions). Gating may return the card to its unit hand on an
      // illegal cell. Only finalize the play if it survived.
      host.addCardToTable(spell, true, { staged: true });
      const mesh = host.meshes.get(spell.id);
      if (!mesh) throw new Error(`castFromUnitHand: mesh not spawned for "${spell.name}"`);
      host._applyFreshPlayGating([{ mesh }], new Set([spell.id]));
      if (!host.meshes.has(spell.id)) return spell.id; // gating returned it to the unit hand
      host._finalizeFreshPlayCommit(spell, { mesh });
      return spell.id;
    },

    // The currently-inspected card (Space / inspector), or null.
    inspectedCard() {
      const c = host.state.inspectedCard;
      return c ? { id: c.id, name: c.name } : null;
    },

    // Hover a unit-hand card then fire the real inspect hotkey (Space). A
    // unit-hand card is a DOM overlay, so this exercises the host
    // _hoveredUnitSpell wiring the hotkey resolves its target from.
    inspectUnitSpell(ownerIdOrName, spellName) {
      const { spell } = api._resolveUnitSpell(ownerIdOrName, spellName);
      host._hoveredUnitSpell = spell;
      host.dispatchHotkeyAction('card.inspect', { preventDefault() {}, repeat: false });
      return spell.id;
    },

    // Locate a spell sitting in a unit hand: { ownerId, spell }.
    _resolveUnitSpell(ownerIdOrName, spellName) {
      const owner = findCard(host, ownerIdOrName);
      const ownerId = owner?.id || ownerIdOrName;
      const entry = host.state.gameState?.unitHands?.[ownerId];
      const spell = (entry?.spells || []).find((s) => s.name === spellName || s.id === spellName);
      if (!spell) throw new Error(`unit-hand spell "${spellName}" not in "${ownerId}"`);
      return { ownerId, spell };
    },

    // Begin a drag out of a unit hand (the real gesture): the spell leaves
    // the hand and stages on the table (tracked by host._unitHandDrag).
    // Returns the spell id. Pair with cancelUnitHandDrag /
    // dropUnitHandDragOnPlayerHand, or let it commit elsewhere.
    beginUnitHandDrag(ownerIdOrName, spellName) {
      const { ownerId, spell } = api._resolveUnitSpell(ownerIdOrName, spellName);
      host.beginUnitHandDrag({ button: 0, clientX: 0, clientY: 0, preventDefault() {} }, spell, ownerId);
      return spell.id;
    },

    // The FULL real gesture: begin a drag out of the unit hand (keep-in-hand
    // + stage), then commit it on a cell (drop). Exercises the keep-in-hand
    // → finalizeFreshPlayCommit path the live drag takes, so we catch a
    // played card that wrongly lingers in the unit hand.
    dragCastFromUnitHand(ownerIdOrName, spellName, { col, row } = {}) {
      const { ownerId, spell } = api._resolveUnitSpell(ownerIdOrName, spellName);
      host.beginUnitHandDrag({ button: 0, clientX: 0, clientY: 0, preventDefault() {} }, spell, ownerId);
      const world = host._cellCenterWorld(col, row);
      const mesh = host.meshes.get(spell.id);
      if (mesh) { mesh.position.x = world.x; mesh.position.z = world.z; }
      spell.x = world.x; spell.z = world.z;
      const full = host._getFullCard(spell.cardId);
      if (full?.type === 'Magic') return host._castMagicFromHand(spell);
      host._finalizeFreshPlayCommit(spell, { mesh });
      return spell.id;
    },

    // The card currently mid-drag out of a unit hand (it has left the hand
    // and is staged on the table; tracked by host._unitHandDrag).
    _inflightDragSpell() {
      const id = host._unitHandDrag?.spellId;
      const spell = id ? host.meshes.get(id)?.userData?.cardInstance : null;
      if (!spell) throw new Error('no in-flight unit-hand drag');
      return spell;
    },

    // Cancel an in-flight unit-hand drag (dragged back over the badge): the
    // spell returns to its unit hand, never the player hand.
    cancelUnitHandDrag() {
      const spell = api._inflightDragSpell();
      host.returnDragToUnitHand(spell);
      return spell.id;
    },

    // Drag an in-flight unit-hand spell onto the player hand: it must bounce
    // back to its unit hand (an owner-locked spell may never enter the hand).
    dropUnitHandDragOnPlayerHand() {
      const spell = api._inflightDragSpell();
      host.handOffDragToHand(spell, { clientX: 0, clientY: 0 });
      return spell.id;
    },

    // Faithful reproduction of the REAL drag-from-hand gesture: stage a mesh
    // at the drop cell, then run _finalizeFreshPlayCommit. Exactly what
    // pointerInteractions.handleMouseUp does, with NO type special-casing.
    // This is the production path; use it to verify that a Magic dropped on
    // the board does NOT illegally enter the realm.
    dragPlayFromHand(idOrName, { col, row }) {
      const card = findCard(host, idOrName);
      if (!card) throw new Error(`dragPlayFromHand: card not in hand "${idOrName}"`);
      const world = host._cellCenterWorld(col, row);
      card.x = world.x; card.y = 0; card.z = world.z;
      host.removeFromHand(card);
      host.addCardToTable(card, true, { staged: true });
      const mesh = host.meshes.get(card.id);
      if (mesh) host._finalizeFreshPlayCommit(card, { mesh });
      return card.id;
    },

    tap(idOrName, value = true) {
      const card = findCard(host, idOrName);
      const mesh = host.meshes.get(card.id);
      host._applyTap(card, mesh, value, { source: 'activate', log: true });
      return card.id;
    },

    surface(idOrName) {
      const card = findCard(host, idOrName);
      host._setSubsurface(card, null);
      return card.id;
    },

    // Drop an equipped card off its carrier through the real detach path
    // (the inspector's Drop button -> host.detachEquipment). The dropped
    // card re-spawns on the field; a subsurface carrier's region is copied
    // onto it. `equipId` is the equipped card's id (e.g. equip()'s return).
    detach(carrierIdOrName, equipId) {
      const carrier = findCard(host, carrierIdOrName);
      if (!carrier) throw new Error(`detach: carrier "${carrierIdOrName}" not found`);
      host.detachEquipment(carrier, equipId);
      return equipId;
    },

    // Atomic positional primitive: move a unit to a cell through the real
    // forced-move path. Fires on_stop / on_enter_location (and the
    // first-visit / corner machinery) exactly as a live move would. The
    // reusable driver for any positional-trigger card (Wayfaring Pilgrim's
    // corner draw, on_enter_location watchers, etc). Units can't land in
    // the void, so the destination cell needs a site under it.
    move(idOrName, { col, row }) {
      const card = findCard(host, idOrName);
      if (!card) throw new Error(`move: "${idOrName}" not found`);
      // A fresh cast marks the card intent-owned (the cast intent committed
      // its SET_POSITION); the live drag's release consumes that one-shot
      // flag. The bridge play path has no release, so consume it here or the
      // move's own authoritative SET_POSITION gets swallowed and the next
      // mirror fold snaps the card back.
      host._intentOwnedCards?.delete(card.id);
      // Full cell (centerX/centerZ + dims) like the live path. A bare
      // {col,row} logs "to [NaN]" (see pickCell).
      const world = host._cellCenterWorld(col, row);
      const fullCell = (world && host._cellAtWorld(world.x, world.z)) || { col, row };
      host._moveCardToCell(card, fullCell, 'test move', k('moves'), 'forced');
      return card.id;
    },

    // Atomic removal primitive: send a card to its owner's cemetery through
    // the real pile path, which fires on_death / Deathrite (Kettletop
    // Leprechaun's "Deathrite → draw a site"). Reusable for any death /
    // deathrite card without needing a combat kill to set it up.
    kill(idOrName) {
      const card = findCard(host, idOrName);
      host.sendCardToPile(card, 'Cemetery');
      return card.id;
    },

    // Flip a card's controller through the real control-change path
    // (recomputes auras, reconciles sickness, flips any owned unit hand).
    flipControl(idOrName) {
      const card = findCard(host, idOrName);
      const mesh = host.meshes.get(card.id);
      host._applyControllerFlip(card, mesh, !card.rotated, { broadcast: false, log: false });
      return card.id;
    },

    async activate(idOrName, entryIndex = 0) {
      const card = findCard(host, idOrName);
      const entries = host._activatedEntriesFor(card) || [];
      const entry = entries[entryIndex];
      if (!entry) throw new Error(`activate: no activated ability[${entryIndex}] on "${idOrName}"`);
      host._activateAbility(card, entry);
      // The activation resolves immediately (the click IS the confirm). The
      // resolver may route a parser-emitted entry through applyCardAutomation,
      // which posts its OWN generic `confirm`/`draw` prompt for the same card
      // (e.g. Necromancer's summon). Flush a round, then auto-resolve that
      // follow-on so scenarios reach the post-confirm interaction (cell pick /
      // summon placement).
      const flush = () => new Promise((resolve) => host.setState({}, resolve));
      await flush();
      const followon = (host.state.pendingPrompts || []).find(
        (p) => (p.promptType === 'confirm' || p.promptType === 'draw') && p.cardInstance?.id === card.id,
      );
      if (followon) host.handlePromptConfirm(followon.id, 'confirm');
      return card.id;
    },

    // Arm the Animist's "summon a magic as a Spirit" ability. The next
    // Magic played from hand this turn enters the realm as a Spirit Minion
    // (power = cost) instead of casting to the storyline.
    async armAnimistSpirit(avatarIdOrName = 'Animist') {
      const avatar = findCard(host, avatarIdOrName);
      if (!avatar) throw new Error(`armAnimistSpirit: avatar "${avatarIdOrName}" not found`);
      const entry = (host._activatedEntriesFor(avatar) || []).find((e) => e._builtin === 'animist_spirit');
      if (!entry) throw new Error('armAnimistSpirit: Animist has no animist_spirit ability');
      host._activateAbility(avatar, entry);
      await new Promise((resolve) => host.setState({}, resolve));
      return avatar.id;
    },

    // Resolve a pending pick_caster prompt by choosing the named
    // Spellcaster (the value is the caster's mesh id). Opens when 2+
    // valid spellcasters exist for a spell.
    pickCaster(casterName) {
      const queue = host.state.pendingPrompts || [];
      const prompt = queue.find((p) => p.promptType === 'pick_caster');
      if (!prompt) throw new Error('pickCaster: no pending pick_caster prompt');
      const caster = findCard(host, casterName);
      if (!caster) throw new Error(`pickCaster: caster "${casterName}" not found`);
      host.handlePromptConfirm(prompt.id, caster.id);
      return caster.id;
    },

    equip(equipIdOrName, targetIdOrName) {
      const equip = findCard(host, equipIdOrName);
      const target = findCard(host, targetIdOrName);
      host.equipCardTo(equip, { id: target.id });
      return equip.id;
    },

    // ── Prompt resolution ────────────────────────────────────────
    confirmPrompt(promptId, value) {
      host.handlePromptConfirm(promptId, value);
    },

    // Decline / cancel a prompt (the drawer's Cancel/Decline button). The
    // production path for the "may" decline branch of optional triggers.
    cancelPrompt(promptId) {
      host.handlePromptCancel(promptId);
    },

    // Cradle of Etherrum: cast the Dragon offered on the scry top for (1)
    // less (the scry session's castOption). Mirrors the ScryDialog button.
    scryCastTop() {
      host.scryCastFromTop();
    },

    confirmTopPrompt(value) {
      const queue = host.state.pendingPrompts || [];
      const top = queue[queue.length - 1];
      if (!top) throw new Error('confirmTopPrompt: no pending prompt');
      host.handlePromptConfirm(top.id, value);
      return top.id;
    },

    resolveSphinx(cardId) {
      const session = host.state.pendingPileSelect;
      if (!session) throw new Error('resolveSphinx: no pending pile-select');
      let card = findCard(host, cardId);
      if (!card) {
        for (const pile of host.state.gameState.piles || []) {
          const c = (pile.cards || []).find((x) => x.id === cardId || x.name === cardId);
          if (c) { card = c; break; }
        }
      }
      if (!card) throw new Error(`resolveSphinx: card not found "${cardId}"`);
      // Sphinx rides the canonical multi-select. Mutate the live selection
      // synchronously (bridge-only): _togglePileSelectCard goes through async
      // setState, so reading selectedIds back after the second toggle sees a
      // stale length-1 and the pair never auto-confirms. Once the second copy
      // lands the pair is complete, so confirm to banish both + draw.
      session.selectedIds = [...(session.selectedIds || []), card.id];
      if (session.selectedIds.length >= 2) host._confirmPileSelect();
    },

    // Open Archimago's "banish 3 magic spells → cast a copy" picker.
    startArchimagoCopy(avatarIdOrName) {
      const avatar = findCard(host, avatarIdOrName);
      if (!avatar) throw new Error(`startArchimagoCopy: "${avatarIdOrName}" not found`);
      host._startArchimagoCastCopy(avatar, avatar.rotated ? 'p2' : 'p1');
      return avatar.id;
    },

    // Resolve one Archimago pick from the active picker. Routes to the
    // banish phase (first 3 picks) then the copy phase. Picks by name from
    // the current searchPile.
    resolveArchimagoPick(cardName) {
      const pile = host.state.searchPile;
      const card = (pile?.cards || []).find((c) => c.name === cardName || c.id === cardName);
      if (!card) throw new Error(`resolveArchimagoPick: "${cardName}" not in picker`);
      if (host.state.pendingArchimagoBanish) host._resolveArchimagoBanishPick(card);
      else if (host.state.pendingArchimagoCopy) host._resolveArchimagoCopyPick(card);
      else throw new Error('resolveArchimagoPick: no Archimago pick pending');
      return card.id;
    },

    // Resolve a pending hand-discard pick (_pendingDiscardCost). Used by
    // discard COSTS (Sisters of Avalon) and discard EFFECTS (Wizard's Den).
    // Picks the named spell from the discarding seat's hand.
    discardPick(cardName) {
      const pd = host._pendingDiscardCost;
      if (!pd) throw new Error('discardPick: no pending discard');
      const hand = pd.controller === 'p2' ? host.state.handCards2 : host.state.handCards;
      const card = (hand || []).find((c) => c.name === cardName || c.id === cardName);
      if (!card) throw new Error(`discardPick: "${cardName}" not in ${pd.controller} hand`);
      host._resolveDiscardCostPick(card);
      // An ineligible pick (off the server's candidate list) is consumed with
      // a toast and leaves the prompt staged. Fail HERE with the card name,
      // not several scenario steps later on an unrelated assertion.
      if (host._pendingDiscardCost === pd) {
        throw new Error(`discardPick: "${cardName}" rejected (not an eligible candidate)`);
      }
      return card.id;
    },

    // Resolve the active cell-pick (pendingCellTarget) at a known cell,
    // bypassing the raycast the pointer handler uses. Mirrors
    // tryResolveCellTarget's commit path.
    pickCell(col, row) {
      const pct = host.state.pendingCellTarget;
      if (!pct) throw new Error('pickCell: no pending cell target');
      const key = `${col},${row}`;
      if (!pct.validCells.has(key)) {
        throw new Error(`pickCell: ${key} not in valid set [${[...pct.validCells].join(' ')}]`);
      }
      // Pass the FULL cell (centerX/centerZ + dims) the live path uses.
      // _moveCardToCell / getCellNumber need world coords, not a bare
      // { col, row } (which logs "to [NaN]" and mis-moves).
      const world = host._cellCenterWorld(col, row);
      const fullCell = host._cellAtWorld(world.x, world.z) || { col, row };
      // Mirror tryResolveCellTarget: a resolver may stage a follow-on cell
      // pick (Flanking Maneuver source→knight-dest) and flip
      // _cellChainInFlight. Skip the null so the staged pick survives.
      host._cellChainInFlight = false;
      host._executeCellOp(pct, fullCell);
      host._dequeuePickCellPrompt(pct.promptId);
      if (host._cellChainInFlight) { host._cellChainInFlight = false; return key; }
      // Dispose the source spell on resolve (a cast Magic resolving via a
      // cell-pick. Blink, Fire Harpoons. Reaches the cemetery here), same
      // as the live tryResolveCellTarget.
      host.setState({ pendingCellTarget: null }, () => {
        host._maybeDisposeFinishedSpell?.(pct.sourceCard?.id, pct.controller);
        host._maybeResumeDeferredTurnPass?.();
      });
      return key;
    },

    // Read the pending cell-target prompt (Midland Army's location pick,
    // Persecutor's step). Its legal cells as a 'col,row' list. null when
    // no cell pick is open. Lets a test prove an effect recognised its
    // targets (e.g. Persecutor found an Evil to step toward) before
    // resolving the pick with pickCell.
    cellTarget() {
      const pct = host.state.pendingCellTarget;
      if (!pct) return null;
      return { op: pct.op, validCells: [...(pct.validCells || [])] };
    },

    // Read the pending summon-placement queue (Necromancer Skeleton,
    // Midland Army Foot Soldiers, etc.): the token name, how many spawns
    // remain, and the legal cells for the head spawn. null when idle.
    summonPlacement() {
      const p = host.state.pendingSummonPlacement;
      if (!p) return null;
      const head = p.queue?.[0];
      return {
        tokenName: p.tokenName,
        remaining: p.queue?.length || 0,
        validCells: head ? [...(head.validCells || [])] : [],
      };
    },

    // Resolve one summon-placement click at a grid cell. The click-to-
    // place flow Necromancer / Midland Army / Invasion enter after a
    // summon op. Spawns the head token at (col,row) and dequeues; call
    // once per queued spawn. Drives the real `tryResolveSummonPlacement`
    // via a cell override (no raycast in headless).
    summonAt(col, row) {
      if (!host.state.pendingSummonPlacement) {
        throw new Error('summonAt: no pending summon placement');
      }
      const ok = host._tryResolveSummonPlacement(null, { col, row });
      if (!ok) throw new Error(`summonAt: cell ${col},${row} rejected`);
      return `${col},${row}`;
    },

    // Resolve a pending UNIT target (pendingTarget). The card-click halo
    // the UI uses for "target minion/avatar" effects. The target may be
    // on the board, in hand, or equipped.
    pickTarget(idOrName) {
      const card = findCard(host, idOrName);
      if (!card) throw new Error(`pickTarget: target not found "${idOrName}"`);
      host._tryResolveTarget(card);
      return card.id;
    },

    // Resolve a tutor / search dialog (pendingSearchEffect) by picking a
    // named card from the pile being searched.
    resolveSearchPick(cardName) {
      const eff = host.state.pendingSearchEffect;
      if (!eff) throw new Error('resolveSearchPick: no pending search effect');
      const pile = host.state.gameState.piles.find((p) => p.id === eff.pileId);
      const card = (pile?.cards || []).find((c) => c.name === cardName || c.id === cardName);
      if (!card) throw new Error(`resolveSearchPick: "${cardName}" not in ${pile?.name}`);
      host._resolveSearchPick(card);
      return card.id;
    },

    // Drive a pile multi-select dialog (pendingPileSelect): toggle each
    // named card, then accept. Empty list accepts zero (for "up to N").
    pileSelect(cardNames = []) {
      const session = host.state.pendingPileSelect;
      if (!session) throw new Error('pileSelect: no pending pile-select');
      const pile = host.state.searchPile
        || host.state.gameState.piles.find((p) => p.id === session.pileId);
      const ids = [];
      for (const n of cardNames) {
        const card = (pile?.cards || []).find((c) => c.name === n || c.id === n);
        if (!card) throw new Error(`pileSelect: "${n}" not in ${pile?.name}`);
        ids.push(card.id);
      }
      // Set the whole selection synchronously on the live session.
      // _togglePileSelectCard one-by-one goes through async setState, so each
      // toggle (and the confirm) reads stale selectedIds and an empty/partial
      // selection commits. Mutating the live session is bridge-only.
      session.selectedIds = ids;
      host._confirmPileSelect();
    },

    // Close an open pile-multi-select dialog via the X button. Routes through
    // host.handlePileSearchClose -> _cancelPileSelect, which disposes an
    // in-flight cast to the cemetery (Black Mass with no eligible pick).
    cancelPileSelect() {
      host.handlePileSearchClose();
    },

    // Swap the open pile-select's searchPile to the opponent (p2) cemetery,
    // the cemetery tab in a two-cemetery span (Return to Nature). Mirrors the
    // dialog tab click -> host.openPileSearch(opponentCemetery).
    openOpponentCemetery() {
      host.openPileSearch(host.findOrCreateCemetery(true));
    },

    scryKeepDone() { host.scryKeepAndDone(); },
    scryAssign(dest) { host.scryAssignCurrent(dest); },
    scryPeekNext() { host.scryPeekNext(); },

    // ── Combat ───────────────────────────────────────────────────
    // Declare an attack from `attackerName` onto `targetName` (a unit,
    // Avatar, or Site in range) and resolve it through the real combat
    // flow. Defender window, sweep, on_strike / on_kill / on_death
    // triggers all fire. `defenders` lists defender card names the
    // opponent commits (default none). Drives _beginCombatAction →
    // _resolveAttackAction (the modal's onPick) → _combatSubmitDefenders.
    attack(attackerName, targetName, { defenders = [] } = {}) {
      const attacker = findCard(host, attackerName);
      const target = findCard(host, targetName);
      if (!attacker) throw new Error(`attack: attacker "${attackerName}" not found`);
      if (!target) throw new Error(`attack: target "${targetName}" not found`);
      // Sorcery melee = move-and-attack: the strike envelope is the
      // attacker's OWN cell (getAttackableCells), so a unit attacks by
      // moving ONTO the target's square. Co-locate the attacker there
      // synchronously (a real move animates), compute the strike records,
      // then declare the fight as _resolveAttackAction does. The attack
      // modal is set via async setState, so we bypass it.
      const tmesh = host.meshes.get(target.id);
      const amesh = host.meshes.get(attacker.id);
      if (!tmesh) throw new Error(`attack: target "${targetName}" not on the board`);
      if (!amesh) throw new Error(`attack: attacker "${attackerName}" not on the board`);
      amesh.position.x = tmesh.position.x;
      amesh.position.z = tmesh.position.z;
      attacker.x = tmesh.position.x;
      attacker.z = tmesh.position.z;
      const acell = host._cellAtWorld(amesh.position.x, amesh.position.z);
      const recs = host._combatTargetsFor(attacker, acell, 0) || [];
      const rec = recs.find((r) =>
        r.target?.card?.id === target.id || r.target?.card?.name === targetName
        || r.target?.site?.name === targetName);
      if (!rec) {
        throw new Error(`attack: "${targetName}" not strikable [${recs.map((r) => r.target?.card?.name || r.target?.site?.name || '?').join(' ')}]`);
      }
      host._declareAttackFromRec(attacker.id, rec);
      // A defender window may be open (sandbox: the opponent seat is local).
      if (host._activeCombatPrompt || host.state.gameState?.pendingCombat) {
        const ids = defenders.map((n) => findCard(host, n)?.id).filter(Boolean);
        host._combatSubmitDefenders(ids, false);
      }
      return attacker.id;
    },

    // Declare an attack WITHOUT auto-answering the defender window. The
    // defend/intercept prompt stays up so a GUI harness can drive the actual
    // prompt DOM (the combat modal's Decline/Defend buttons). Same co-locate +
    // _declareAttackFromRec path as attack() above.
    declareAttack(attackerName, targetName) {
      const attacker = findCard(host, attackerName);
      const target = findCard(host, targetName);
      if (!attacker) throw new Error(`declareAttack: attacker "${attackerName}" not found`);
      if (!target) throw new Error(`declareAttack: target "${targetName}" not found`);
      const tmesh = host.meshes.get(target.id);
      const amesh = host.meshes.get(attacker.id);
      if (!tmesh) throw new Error(`declareAttack: target "${targetName}" not on the board`);
      if (!amesh) throw new Error(`declareAttack: attacker "${attackerName}" not on the board`);
      amesh.position.x = tmesh.position.x;
      amesh.position.z = tmesh.position.z;
      attacker.x = tmesh.position.x;
      attacker.z = tmesh.position.z;
      const acell = host._cellAtWorld(amesh.position.x, amesh.position.z);
      const recs = host._combatTargetsFor(attacker, acell, 0) || [];
      const rec = recs.find((r) =>
        r.target?.card?.id === target.id || r.target?.card?.name === targetName
        || r.target?.site?.name === targetName);
      if (!rec) {
        throw new Error(`declareAttack: "${targetName}" not strikable [${recs.map((r) => r.target?.card?.name || r.target?.site?.name || '?').join(' ')}]`);
      }
      host._declareAttackFromRec(attacker.id, rec);
      return attacker.id;
    },

    // Recompute static auras / granted triggers. `place()` seeds a card
    // without running the recompute, so granted activated abilities
    // (Salmon's Tap→roll on a co-located ally) need this before activate.
    recomputeAuras() { host.recomputeStaticAuras(); },



    // ── RNG ──────────────────────────────────────────────────────
    // Force the next die rolls. `value` is a fixed 0..1 fraction the
    // engine's rng() returns; rollDie(sides) = floor(value*sides)+1.
    // value 0.99 → max roll (d20 → 20); 0 → min (1). Reset by passing null.
    setRng(value) {
      host._silentOpsHost.rng = value == null ? (() => Math.random()) : (() => value);
    },

    // ── Turn control ─────────────────────────────────────────────
    passTurn() { host.passTurn(); },

    // Pass the turn and resolve the incoming player's mandatory start
    // draw + any leading confirm so the board settles on the new turn.
    // Flush one async setState round so START-phase prompts that post in a
    // `setState({}, cb)` callback (on_turn_start triggers like Seer's scry)
    // become visible before the harness reads/drains them.
    async flush() {
      await new Promise((resolve) => host.setState({}, resolve));
    },

    async passTurnResolvingDraws() {
      // passTurn commits the switch + incoming START phase inside a
      // `setState({}, cb)` callback, which Preact runs ASYNCHRONOUSLY.
      // so the start-of-turn draw prompt (and on_turn_start triggers like
      // Bedivere / Seelie Court) post AFTER passTurn() returns. Flush a
      // setState round per iteration so each prompt is visible before we
      // drain it; stop only after two consecutive empty flushes so we
      // never quit ahead of the async START phase posting its prompt.
      const flush = () => new Promise((resolve) => host.setState({}, resolve));
      const drain = async () => {
        let emptyRounds = 0;
        for (let i = 0; i < 16 && emptyRounds < 2; i++) {
          await flush();
          // Auth: the start draw is the server's drawPending, rendered
          // outside pendingPrompts (valkenhall-qa#158); passTurn's gate
          // reads it too, so it has to be confirmed here as well.
          const serverDraw = host._localDrawPhaseChoicePrompt?.();
          const q = host.state.pendingPrompts || [];
          const p = serverDraw || q[q.length - 1];
          if (!p) { emptyRounds++; continue; }
          emptyRounds = 0;
          if (p.promptType === 'draw_phase_choice') host.handlePromptConfirm(p.id, 'spellbook');
          else host.handlePromptConfirm(p.id, 'confirm');
        }
      };
      // Drain FIRST: a still-open start-draw prompt from a prior turn (its
      // post can race past a previous drain window) hard-blocks passTurn's
      // `undrawn` gate, silently swallowing the pass.
      await drain();
      // The confirm above is a DRAW command; passTurn's `undrawn` gate reads
      // the server's drawPending, so wait for that diff to fold before
      // passing (otherwise the pass is silently swallowed).
      for (let i = 0; i < 60 && host.state.drawPending; i++) await new Promise((r) => setTimeout(r, 50));
      host.passTurn();
      await drain();
    },
  };

  return api;
}

export function installTestBridge(host) {
  if (typeof window === 'undefined') return;
  // Resolve magic casts synchronously under test. No on-storyline dwell.
  // so assertions can read the post-cast state without waiting on the
  // cosmetic timer. Production leaves _castDwellMs undefined (→ ~900ms).
  host._castDwellMs = 0;
  window.__test = buildTestBridge(host);
}
